<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'
import { token } from '@formkit/utils'
import { getNode } from '@formkit/core'
import type { FormKitNode } from '@formkit/core'
import { useIdentity } from '~/store/identity'
import type { FrontendQuoteFormDestination } from '~/types/quote/quoteFrontendTypes'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'
import getDefaultCountryId from '~/utils/countries/getDefaultCountryId'
import urlRegex from '~/utils/formkit/urlRegex'
import getTabsLimit from '~/utils/forms/getTabsLimit'
import { PagesPrefixEnum } from '~/enums/PagesPrefixEnum'
import { Routes } from '~/enums/Routes'

const storeConfig = useStoreConfig()
const identity = useIdentity()
const { isAlt, isWhitelabel } = useTheme()
const user = await identity.getUser()
const route = useRoute()

const isIndexPage = computed(() => route.name === `${PagesPrefixEnum}${Routes.Index}`)

const { t } = useLang()

defineProps<{
  isSimpleSubmitted?: boolean
}>()

const emit = defineEmits(['onSimpleSubmit'])

const productItems = ref([token()])
const active = ref(productItems.value[0])
const currentTab = ref(0)
const currentPage = ref(1)

const config = useStoreConfig()

const successId = ref<number>()
const blacklistedDomains = ref<string[]>([])
const errorMessage = ref<string>()
const suggestNewsletterSubscriptionToEmail = ref<string | null>(null)

const emailValidationMessages = computed(() => ({
  required: t('validation.required'),
  email: t('validation.email'),
}))

const urlValidationMessages = computed(() => ({
  required: t('validation.required'),
  url: t('validation.url'),
}))

const quantityValidationMessages = computed(() => ({
  required: t('validation.required'),
  number: t('validation.number'),
  min: (context: { args: Array<any>; name: string; node: FormKitNode }) =>
    t('validation.min_number', { value: context.args[0] }),
  max: (context: { args: Array<any>; name: string; node: FormKitNode }) =>
    t('validation.max_number', { max: context.args[0] }),
}))

const destinations = await $fetch<FrontendQuoteFormDestination[]>('/api/quotes/get-destinations', {
  headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
})

const defaultCountryId = getDefaultCountryId() || 0

const quoteData = ref<{
  email: string
  destination?: {
    country_id: number
    name: string
    flag: string
  }
  products: {
    [key: string]: { url: string; quantity: number; comment: string }
  }
}>({
  email: user?.email ?? '',
  destination: destinations?.find((destination) => destination.country_id === defaultCountryId) ?? destinations?.[0],
  products: {
    [productItems.value?.[0]]: {
      comment: '',
      quantity: 1,
      url: '',
    },
  },
})

const submitHandler = async () => {
  blacklistedDomains.value = []
  errorMessage.value = ''

  try {
    const response = await $fetch('/api/quotes/submitQuote', {
      method: 'POST',
      body: {
        email: quoteData.value.email,
        products: Object.keys(quoteData.value.products).map((key) => {
          const product = quoteData.value.products[key]

          return { ...product, url: product.url.trim() }
        }),
        country: quoteData.value.destination?.country_id,
        languageId: storeConfig.queryKeys?.languageId,
      },
      ...fetchRetryParameters,
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
    })

    if (response?.errors) {
      const { promo_error, ...rest } = response.errors || {}
      if (promo_error) {
        errorMessage.value = promo_error
      }

      const errorKey = Object.keys(rest).find((key) => {
        return Object.prototype.hasOwnProperty.call(rest, key)
      })

      errorMessage.value = errorKey ? t(`error.${errorKey}`) : t('error.unknown_error')
    }

    if (response?.blacklistedDomains) {
      blacklistedDomains.value = response.blacklistedDomains

      return await nextTick(() => {
        return testValidity(true)
      })
    }

    if (response?.id) {
      successId.value = response.id

      suggestNewsletterSubscriptionToEmail.value =
        response.newsletter?.subscription.suggest && response.newsletter?.subscription.email
          ? response.newsletter?.subscription.email
          : null
    }
  } catch (error) {}
}

const addProduct = async () => {
  const isValid = await testValidity()

  if (isValid) {
    const newToken = token()
    productItems.value.push(newToken)

    return await nextTick(() => {
      active.value = newToken
      currentTab.value = productItems.value.length - 1
      currentPage.value = productItems.value.length
    })
  }
}

const removeItem = async (key: string, index: number) => {
  productItems.value = productItems.value.filter((item) => item !== key)

  return await nextTick(() => {
    delete quoteData.value.products[key]

    if (index === productItems.value.length) {
      active.value = productItems.value[index - 1]
      currentTab.value = productItems.value.length - 1
      currentPage.value = productItems.value.length
    } else {
      active.value = productItems.value[index]
      currentTab.value = index
      currentPage.value = index + 1
    }
  })
}

const onBack = () => {
  const newToken = token()
  productItems.value = [newToken]
  active.value = newToken
  quoteData.value = {
    destination: destinations?.find((destination) => destination.country_id === defaultCountryId) ?? destinations?.[0],
    email: user?.email || '',
    products: {},
  }
  successId.value = undefined
  suggestNewsletterSubscriptionToEmail.value = null
}

const onDestinationClick = (country: { country_id: number; name: string; flag: string }) => {
  quoteData.value = { ...quoteData.value, destination: country }
}

const checkForInvalidProducts = () => {
  const invalidField = productItems.value.findIndex((product) => {
    const node = getNode(product)

    return node?.children.some(
      (field) => (field.context && !field.context.state.valid) || field.context?.classes?.inner.includes('blacklisted')
    )
  })

  return invalidField
}

const testValidity = async (hasBeenSubmitted?: boolean) => {
  const invalidField = checkForInvalidProducts()
  const node = getNode('quote-products-form')

  if (invalidField >= 0) {
    if (!hasBeenSubmitted) {
      node?.submit()
    }

    return await nextTick(() => {
      active.value = productItems.value?.[invalidField]
      currentTab.value = invalidField
      currentPage.value = invalidField + 1

      return false
    })
  }

  return true
}

const updateData = (tabIndex: number) => {
  currentTab.value = tabIndex - 1
  active.value = productItems.value[tabIndex - 1]
  currentPage.value = tabIndex
}

const limit = computed(() => getTabsLimit(config.isMobile, currentPage.value, productItems.value.length))

const onSimpleFormSubmit = () => {
  emit('onSimpleSubmit')
}
</script>

<template>
  <div
    class="quotes-form"
    :class="{
      'wl-index': isWhitelabel && isIndexPage,
    }">
    <LazyOptinModal
      v-if="!isAlt && suggestNewsletterSubscriptionToEmail"
      :email="suggestNewsletterSubscriptionToEmail" />

    <QuoteSuccess v-if="successId" :success-id="successId" back @on-back="onBack" />

    <div v-else class="flex flex-col gap-4 w-full h-full">
      <div v-if="isWhitelabel && !isSimpleSubmitted && isIndexPage">
        <div
          class="pb-3 text-[18px] text-text2 dark:text-dmText2 font-semibold px-4"
          v-text="t('quotes.products-to-buy')" />
        <FormKit
          id="quote-simple-form"
          v-slot="{ disabled }"
          type="form"
          :actions="false"
          :incomplete-message="false"
          validation="required"
          :classes="{
            form: {
              'flex lg:gap-4 px-4 items-center flex-col lg:flex-row': true,
            },
          }"
          @submit="onSimpleFormSubmit">
          <FormKit
            v-model="quoteData.products[Object.keys(quoteData.products)[0]].url"
            :classes="{ outer: { 'flex-1 mb-0 w-full ': true } }"
            :label="t('quotes.product-url')"
            type="text"
            :validation-messages="urlValidationMessages"
            :validation="'required|url'"
            :validation-rules="{ url: urlRegex }"
            placeholder="https://www.amazon.com/..." />
          <Button class="w-full lg:!w-min !h-min !min-h-9" submit :disabled="disabled">
            {{ t('common.continue') }} <i class="fas fa-chevron-right" />
          </Button>
        </FormKit>
      </div>
      <FormKit
        v-else
        id="quote-products-form"
        v-slot="{ disabled }"
        v-model="quoteData"
        type="form"
        :actions="false"
        :incomplete-message="false"
        :classes="{
          form: {
            'flex flex-col overflow-hidden h-full': true,
          },
        }"
        validation="required"
        validation-visibility="live"
        @submit="submitHandler">
        <div class="px-5">
          <span v-if="!storeConfig.getIsMobile" class="h6 form-heading" v-text="t('quotes.products-to-buy')" />
          <div
            v-if="errorMessage"
            class="errors flex items-center mb-6 px-4 py-[0.625rem] bg-redLight font-bold text-text1 dark:text-dmText1">
            <i class="fa-solid fa-circle-exclamation text-redDark text-xl mr-3" />
            {{ errorMessage }}
          </div>
          <div v-if="blacklistedDomains.length" class="errors flex items-center mb-6 px-4 py-[0.625rem] bg-redLight">
            <i class="fa-solid fa-circle-exclamation text-redDark text-xl mr-3" />
            <div class="flex flex-col gap-2">
              <div v-for="(domain, index) in blacklistedDomains" :key="index">
                <b class="text-black dark:text-white"> {{ domain.toUpperCase() }}: </b>
                <span class="text-text1 dark:text-dmText1">
                  {{ t('error.domain-blacklisted') }}
                </span>
              </div>
            </div>
          </div>

          <div class="section-title" v-text="t('quotes.your-information')" />
          <div class="customer-info flex flex-col md:flex-row flex-wrap gap-2 lg:flex-nowrap">
            <FormKit
              name="email"
              :label="t('quotes.your-email')"
              type="email"
              validation="required|email"
              :validation-messages="emailValidationMessages"
              :placeholder="t('quotes.email-placeholder')"
              :classes="{ outer: { 'flex-1 ': true } }"
              :sections-schema="{
                prefix: {
                  $el: 'div',
                  attrs: { class: 'fa-solid fa-envelope input-prefix ' },
                },
              }"
              @keydown.stop="(e) => e.code === 'Enter' && e.preventDefault()" />
            <div class="destination-select flex flex-col">
              <label class="formkit-label !font-normal">{{ t('quotes.destination-country') }}</label>
              <ClientOnly>
                <USelectMenu
                  v-model="quoteData.destination"
                  :ui="{
                    base: 'formkit-inner destination text-text2 dark:text-dmText2 bg-white dark:bg-dmGray1 flex',
                    container: 'dropdown-wrapper',
                    rounded: 'rounded-sm',
                    option: {
                      rounded: 'rounded-sm',
                    },
                    height: 'rounded-sm',
                  }"
                  value-attribute="data"
                  :options="destinations.map((dest) => ({ data: dest }))"
                  :popper="{ placement: 'bottom-start', arrow: true }">
                  <template #label>
                    <div class="text-text2 dark:text-dmText2 outline-none bg-transparent flex gap-2 wl:lg:text-white">
                      <template v-if="quoteData.destination?.name">
                        <img
                          :key="quoteData.destination?.name"
                          :src="quoteData.destination?.flag"
                          :alt="quoteData.destination?.name" />
                        <span class="ml-2 mr-5">
                          {{ t(`countries.${quoteData.destination?.name.toLocaleLowerCase()}`) }}
                        </span>
                      </template>
                      <template v-else>
                        <span class="ml-2 mr-5">&nbsp;</span>
                      </template>
                    </div>
                  </template>

                  <template #option="{ option: destination }">
                    <div
                      :class="{
                        'dropdown-item': true,
                        active: destination.data.country_id === quoteData.destination?.country_id,
                      }"
                      @click="onDestinationClick(destination.data)">
                      <div class="flex items-center gap-2">
                        <img :src="destination.data.flag" loading="eager" />
                        {{ t(`countries.${destination.data.name.toLocaleLowerCase()}`) }}
                      </div>
                    </div>
                  </template>
                </USelectMenu>
              </ClientOnly>
            </div>
          </div>
          <hr class="upper" />
        </div>
        <div class="tabs">
          <div class="products-section-title mb-2" v-text="t('common.products')" />

          <div class="tabs-wrapper flex overflow-x-auto w-full">
            <UPagination
              v-model="currentPage"
              :page-count="1"
              :total="productItems.length"
              :max="limit"
              :ui="{
                base: 'pagination-item disabled:!hidden ring-0',
                wrapper: 'bg-white dark:bg-dmGray1 gap-[2px]',
                default: {
                  size: 'sm',
                  activeButton: {
                    class: '-active',
                  },
                  nextButton: {
                    class: '!disabled:hidden',
                  },
                },
              }"
              @update:model-value="updateData" />
            <div
              v-if="!config.getIsMobile && productItems.length < 30"
              key="add-product"
              class="pagination-item"
              @click="addProduct">
              <i class="fas fa-plus text-sm" />
            </div>
          </div>
          <UTabs
            v-model="currentTab"
            :items="productItems.map((item) => ({ label: item }))"
            class="w-full"
            :ui="{
              base: 'tab-body',
              container: '!mt-0',
              list: {
                base: 'hidden',
              },
            }">
            <template #item="{ item, index }">
              <FormKit :id="item.label" :key="item.label" v-model="quoteData.products[item.label]" type="group">
                <div class="flex gap-2">
                  <FormKit
                    name="url"
                    :label="t('quotes.product-url')"
                    type="text"
                    :validation-messages="urlValidationMessages"
                    :validation="'required|url'"
                    :validation-rules="{ url: urlRegex }"
                    placeholder="https://www.amazon.com/..."
                    :classes="{
                      outer: {
                        'flex-1 product-input': true,
                      },
                      inner: {
                        blacklisted: Boolean(
                          blacklistedDomains.find((domain) => quoteData.products[item.label].url?.includes(domain))
                        ),
                      },
                    }"
                    :sections-schema="{
                      prefix: {
                        $el: 'div',
                        attrs: { class: 'fa-solid fa-link input-prefix ' },
                      },
                    }"
                    @keydown.stop="(e) => e.code === 'Enter' && e.preventDefault()" />
                  <FormKit
                    name="quantity"
                    :label="t('common.quantity')"
                    type="number"
                    :validation-messages="quantityValidationMessages"
                    validation="required|number|min:1|max:50"
                    :value="1"
                    :min="1"
                    :classes="{ outer: { 'min-w-[80px] max-w-[80px] lg:max-w-[120px]': true } }"
                    @keydown.stop="(e) => e.code === 'Enter' && e.preventDefault()" />
                </div>
                <FormKit
                  name="comment"
                  :placeholder="t('quotes.comment-description')"
                  :label="t('common.comment')"
                  :maxlength="200"
                  type="text"
                  :classes="{ outer: { 'flex-1': true } }"
                  :sections-schema="{
                    prefix: {
                      $el: 'div',
                      attrs: { class: 'fa-solid fa-comment input-prefix ' },
                    },
                  }"
                  @keydown.stop="(e) => e.code === 'Enter' && e.preventDefault()" />
                <div class="flex justify-between gap-4 flex-wrap py-4 lg:py-2 alt:flex-col alt:lg:flex-row">
                  <Button
                    v-if="productItems.length < 30"
                    button-type="secondary"
                    class="flex items-center hover:text-text2 dark:text-dmText2 flex-1 justify-center !py-4"
                    small
                    :disabled="disabled"
                    @click="addProduct">
                    <i class="fas fa-plus-circle pr-2 text-base" />
                    <span> {{ config.getIsMobile ? t('common.add') : t('quotes.add-another-product') }} </span>
                  </Button>
                  <Button
                    v-if="productItems.length > 1"
                    button-type="secondary"
                    class="flex items-center hover:text-text2 dark:text-dmText2 flex-1 justify-center !py-4"
                    small
                    :disabled="disabled"
                    @click.prevent="removeItem(item.label, index)">
                    <i class="fas fa-trash pr-2 text-base" />
                    <span> {{ t('common.remove') }} </span>
                  </Button>
                </div>
              </FormKit>
            </template>
          </UTabs>
        </div>

        <div class="flex items-center justify-center flex-col px-5">
          <Button submit :disabled="disabled" @click="testValidity">{{ t('quotes.get-offer') }}</Button>
        </div>
      </FormKit>
      <div v-if="!(isWhitelabel && !isSimpleSubmitted && isIndexPage)" class="px-4 lg:px-5">
        <QuoteDisclaimer />
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
.quotes-form {
  @apply py-4 w-full lg:py-5 lg:max-w-[35rem] lg:max-h-full bg-neutral7 dark:bg-dmGray2;
  @apply rounded-none lg:rounded-md z-20 lg:shadow-md;

  .form-heading {
    @apply flex mb-6 font-bold;
  }
}
</style>

<style lang="postcss" scoped>
.quotes-form {
  form {
    overflow: hidden;
  }

  .formkit-form {
    overflow: hidden;
  }

  :deep(.formkit-outer) {
    .formkit-wrapper {
      .formkit-inner {
        .input-prefix {
          font-size: 1rem;
          padding-left: 0.625rem;
        }
      }
    }

    .formkit-message {
      font-size: 0.75rem;
    }

    &[data-invalid='true'] {
      .formkit-inner {
        outline: 1px solid red !important;
      }
    }
  }

  :deep(.formkit-label) {
    font-size: 0.75rem;
  }

  :deep(.formkit-message) {
    font-size: 0.75rem;
  }

  :deep(.destination-select) {
    img {
      height: 15px;
      border-radius: 3px;
    }

    ul {
      @apply !rounded-sm;
    }

    .destination {
      @apply rounded-[0.375rem] text-base lg:text-sm px-[0.5875rem] py-[0.5625rem] lg:py-[0.65625rem];

      span {
        @apply leading-none;
      }
    }

    ul {
      @apply p-0;

      > li {
        @apply rounded-none p-2;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  hr {
    margin: 1rem 0;
    border-top: theme('vars.borderMedium');

    &.upper {
      margin-top: 0;
    }
  }

  .products {
    overflow-y: auto;

    margin-right: 4px;
  }

  .btn-primary {
    width: 100%;
  }

  .section-title {
    display: flex;
    width: 100%;
    font-weight: 700;
    padding-bottom: 0.75rem;
    font-size: 0.875rem;
  }

  :deep(.blacklisted) {
    box-shadow: 0 0 0 2px theme('colors.redDark') !important;
    background-color: theme('colors.redLight');

    input {
      box-shadow: none;
      -webkit-box-shadow: 0 0 0 30px theme('colors.redLight') inset;
    }
  }

  .errors {
    border-radius: theme('vars.borderRadiusHeavy');
  }

  :deep(.tabs) {
    padding: 0 1.25rem;
    margin-bottom: 1rem;

    .tabs-wrapper {
      @apply bg-neutral7 dark:bg-dmGray2 border dark:border-b-dmGray2 border-neutral5 dark:border-dmGray5;

      border-radius: 8px;
      border-bottom-left-radius: 0px !important;
      border-bottom-right-radius: 0px !important;
      max-width: calc(100vw - 2rem);
      position: relative;

      @screen lg {
        max-width: 520px !important;
      }
    }

    .tab-body {
      @apply bg-white dark:bg-dmGray1 border border-neutral5 dark:border-dmGray5;
      padding: 1rem;
      border-radius: 8px;
      margin-top: -1px;
      border-top-left-radius: 0px !important;
      border-top-right-radius: 0px !important;
      border-top: 0px;
    }

    .pagination-item:first-of-type {
      border-left: 1px solid white;
    }

    .pagination-item:disabled {
      display: none;
    }

    .pagination-item {
      @apply border-white dark:border-dmGray1 bg-neutral7 dark:bg-dmGray2 text-neutral3 dark:text-dmText3;

      font-size: 0.875rem;
      height: 48px;
      width: calc((100vw - 64px) / 7);
      font-weight: 700;
      padding: 0;
      border-radius: 0px;
      margin: 0;
      align-items: center;
      align-content: center;
      text-align: center;
      padding: 0 0.5rem;
      position: relative;
      z-index: 1;
      color: theme('colors.neutral3');
      cursor: pointer;

      @screen lg {
        width: 52px;
      }

      > span {
        width: 100%;
        text-align: center;
      }

      > button {
        position: absolute;
        right: 0.5rem;
      }

      &:hover {
        @apply bg-neutral5 dark:bg-dmGray3;
      }

      &.-active {
        @apply bg-white dark:bg-dmGray1 border-b border-b-white dark:border-b-dmGray1 rounded;
        font-weight: 700;
        z-index: 3;
        color: theme('colors.brand.primary');

        > span {
          width: 100%;
          text-align: center;
        }
      }
    }

    .tab {
      .product-tab {
        font-size: 14px;

        p {
          padding: 0.5rem 0.25rem;
          display: flex;
          gap: 1rem;

          .label {
            white-space: nowrap;
            font-weight: 700;
            min-width: 7rem;
          }

          .value {
            max-width: 100%;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
          }
        }
      }
    }
  }
}

.wl .quotes-form {
  @apply bg-brand-primaryLight border border-brand-primary rounded-md w-full self-start;

  :deep(.tabs-wrapper) {
    @apply bg-brand-primaryLight;

    .pagination-item {
      @apply bg-brand-primaryLight;
    }
  }

  &.wl-index {
    @apply m-4 lg:m-0;
  }
}
</style>
